import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { SocialNetworkProfileServices, AccountOption } from "@/services/bedrock";
import { getErrorMessage, openCenteredPopup, useInterval, useSocialProfiles } from "@/utils";
import { googleDuplicateWarning, GOOGLE_LOCATION_WARNING } from "@/utils/googleLocationWarning";
import { SocialNetworksId, socialNetworkNames } from "@/interfaces";
import { toast } from "sonner";
import {
  isTwoStepNetwork,
  requiresConfigAfterConnection,
  SocialProfileState,
} from "@/components/SocialNetworkProfiles/components";

interface UseSocialConnectionProps {
  userId: string | number;
  networkId: SocialNetworksId;
  handleOptions: (options: AccountOption[]) => void;
  handleCardAlert: (alert?: CardAlert) => void;
  handleConfiguration: () => void;
}

export type CardAlert = {
  type: "error" | "warning" | "success";
  message: string;
  description?: string;
  action?: {
    label: string;
    id: string;
  };
};

export function useSocialConnection({
  userId,
  networkId,
  handleOptions,
  handleCardAlert,
  handleConfiguration,
}: UseSocialConnectionProps) {
  const { socialProfiles, refetchProfile, removeProfileFromCache, isLoading, isFetching } = useSocialProfiles(userId);
  const socialProfile = useMemo(
    () => socialProfiles?.find(p => p.socialNetworkId === networkId),
    [socialProfiles, networkId]
  );
  const socialIdentity = socialProfile?.socialIdentity;
  const socialProfileId = socialProfile?.id;
  const isActive = socialProfile?.isActive;
  const isSyncing = isActive && !socialIdentity;

  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const popupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [hasWarnedExpiration, setHasWarnedExpiration] = useState(false);

  const state: SocialProfileState | undefined = useMemo(() => {
    if (isDisconnecting) return "DISCONNECTING";
    if (isConnecting) return "CONNECTING";
    if (isSyncing) return "SYNCING";
    if (isActive && !!socialIdentity) return "CONNECTED";
    if (!isLoading) return "NOT_LINKED";
    return undefined;
  }, [isLoading, isDisconnecting, isConnecting, isSyncing, isActive, socialIdentity]);

  useInterval(
    () => {
      if (refetchProfile && socialProfileId) refetchProfile(socialProfileId).catch(console.error);
    },
    isSyncing ? 3000 : undefined
  );

  useEffect(() => {
    return () => {
      if (popupIntervalRef.current) {
        clearInterval(popupIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isLoading || state !== "CONNECTED") {
      setHasWarnedExpiration(false);
      return;
    }

    if (socialProfile?.locationWarning) {
      handleCardAlert(reconnectAlert(socialProfile.locationWarning.title, socialProfile.locationWarning.description));
      return;
    }

    if (hasWarnedExpiration || !socialProfile?.expiresAt) return;

    const expirationDate = new Date(socialProfile.expiresAt);
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    if (expirationDate <= thirtyDaysFromNow) {
      const isExpired = expirationDate <= now;
      const daysLeft = Math.max(0, Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

      handleCardAlert(
        reconnectAlert(
          isExpired ? "Connection Expired" : "Connection Expiring Soon",
          isExpired
            ? "Your connection has expired. Please reconnect to restore access."
            : `Your connection will expire in ${daysLeft} day${daysLeft === 1 ? "" : "s"}.`,
          isExpired ? "error" : "warning"
        )
      );

      setHasWarnedExpiration(true);
    }
  }, [
    isLoading,
    state,
    socialProfile?.expiresAt,
    socialProfile?.locationWarning,
    hasWarnedExpiration,
    handleCardAlert,
  ]);

  const activateProfile = useCallback(
    async (newSocialProfileId = socialProfileId, option?: AccountOption) => {
      if (!userId || !newSocialProfileId) return;
      handleCardAlert(undefined);
      setIsConnecting(true);
      try {
        await SocialNetworkProfileServices.activateProfile(userId, newSocialProfileId, {
          selectedAccountId: option?.id,
        });
        await refetchProfile(newSocialProfileId);
        if (requiresConfigAfterConnection(networkId)) {
          handleConfiguration();
        }
      } catch (e: unknown) {
        console.error(e);
        const locationWarning = locationWarningFromError(e);
        handleCardAlert(
          locationWarning ?? {
            type: "warning",
            message: `There was an error activating the connection. Please try again later.`,
            description: getErrorMessage(e),
            action: {
              label: "Need help?",
              id: "SHOW_HELP",
            },
          }
        );
      } finally {
        setIsConnecting(false);
      }
    },
    [userId, socialProfileId, handleCardAlert]
  );

  const cancelActivation = useCallback(() => {
    setIsConnecting(false);
  }, []);

  const pullOptions = useCallback(
    async (newSocialProfileId = socialProfileId) => {
      if (!userId || !newSocialProfileId) return;
      setIsConnecting(true);
      handleCardAlert(undefined);
      const optionType = networkId === "FB" ? "page" : networkId === "GL" ? "Business Profile location" : "account";
      try {
        const options = await SocialNetworkProfileServices.getAccountOptions(userId, newSocialProfileId);
        const selectable = options.filter(option => !option.isDuplicate);
        const unavailable = options.find(option => option.isDuplicate);

        if (selectable.length === 0 && options.length > 0) {
          const warning = unavailable ? googleDuplicateWarning(unavailable, options) : GOOGLE_LOCATION_WARNING;
          handleCardAlert(reconnectAlert(warning.title, warning.description));
          setIsConnecting(false);
        } else if (selectable.length === 1 && options.length === 1) {
          await activateProfile(newSocialProfileId, selectable[0]);
        } else if (selectable.length >= 1) {
          handleOptions(options);
        } else {
          handleCardAlert({
            type: "warning",
            message: `We couldn't find any eligible ${optionType}s to connect.`,
            description: `Make sure you have access to the ${optionType} and that all permissions were granted during setup.`,
            action: {
              label: "Need help?",
              id: "SHOW_HELP",
            },
          });
          setIsConnecting(false);
        }
      } catch (e: unknown) {
        console.error(e);
        setIsConnecting(false);
        handleCardAlert({
          type: "error",
          message: `There was an error fetching your ${optionType} info. Please try again later.`,
          description: getErrorMessage(e),
          action: {
            label: "Need help?",
            id: "SHOW_HELP",
          },
        });
      }
    },
    [userId, networkId, socialProfileId, activateProfile]
  );

  const disconnect = async () => {
    if (!userId || !socialProfileId) return;
    setIsDisconnecting(true);
    handleCardAlert(undefined);
    try {
      await SocialNetworkProfileServices.deleteProfile(userId, socialProfileId);
      removeProfileFromCache(socialProfileId);
    } catch (e: unknown) {
      console.error(e);
      toast.error("There was an error disconnecting your account. Please refresh the page and try again.", {
        description: getErrorMessage(e),
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const connect = () => {
    handleCardAlert(undefined);
    setIsConnecting(true);

    const oauthUrl = `/api/bedrock/integrations/initiate_social_auth/${userId}/networks/${networkId}`;
    let urlWithParams = `${oauthUrl}?origin=web${window.location.origin.includes("localhost") ? "&local=true" : ""}`;

    const popup = openCenteredPopup(urlWithParams, `${socialNetworkNames[networkId]}AuthPopup`, 600, 700);
    if (popupIntervalRef.current) {
      clearInterval(popupIntervalRef.current);
    }
    popupIntervalRef.current = setInterval(() => {
      if (popup?.closed) {
        if (popupIntervalRef.current) {
          clearInterval(popupIntervalRef.current);
        }
        setIsConnecting(false);
      }
    }, 1000);
  };

  useEffect(() => {
    const handlePostMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data.socialNetworkId !== networkId) return;

      if (event.data.type === "social_auth_success") {
        const newSocialProfileId = event.data.socialNetworkProfileId;
        await refetchProfile(newSocialProfileId);

        isTwoStepNetwork(networkId) ? await pullOptions(newSocialProfileId) : await activateProfile(newSocialProfileId);
      } else if (event.data.type === "social_auth_failure") {
        handleCardAlert({
          type: "error",
          message: "There was a problem connecting your account.",
          description: event.data.errorMessage,
        });
      }
    };

    window.addEventListener("message", handlePostMessage);
    return () => window.removeEventListener("message", handlePostMessage);
  }, [networkId, pullOptions, activateProfile, refetchProfile]);

  return {
    state,
    socialProfile,
    socialIdentity,

    isLoading,
    isFetching,
    refetchProfile,

    connect,
    pullOptions,
    activateProfile,
    cancelActivation,
    disconnect,
  };
}

function reconnectAlert(message: string, description: string, type: CardAlert["type"] = "error"): CardAlert {
  return {
    type,
    message,
    description,
    action: {
      label: "Reconnect",
      id: "RECONNECT",
    },
  };
}

function locationWarningFromError(error: unknown): CardAlert | undefined {
  const data = locationWarningPayload(error);
  if (!data?.title || !data?.message) return undefined;

  return reconnectAlert(data.title, data.message, "warning");
}

function locationWarningPayload(error: unknown): { title?: string; message?: string } | undefined {
  if (!error || typeof error !== "object") return undefined;

  const direct = error as { title?: string; message?: string };
  if (direct.title && direct.message) return direct;

  return (error as { response?: { data?: { title?: string; message?: string } } }).response?.data;
}
